/* eslint-disable jsx-a11y/media-has-caption */
import { State } from '@store/index';
import { ICall, TelnyxRTC } from '@telnyx/webrtc';
import { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export interface Call extends ICall {
  remoteStream?: MediaStream;
  state?: string;
  prevState?: string;
  options?: {
    remoteCallerNumber?: string;
  };
  hangup?: () => void;
  muteAudio?: () => void;
  unmuteAudio?: () => void;
  hold?: () => void;
  unhold?: () => void;
  deaf?: () => void;
  undeaf?: () => void;
}

export type CallState = {
  inProgress: boolean;
  isInitiating: boolean;
  isRinging: boolean;
  isHeld: boolean;
  isAnswered: boolean;
  isHungup: boolean;
  isBusy: boolean;
  isPurged: boolean;
  contactNumber?: string;
  isMuted: boolean;
  isDeafened: boolean;
};

export type CallError = {
  message: string;
  code: number;
  type: string;
};

export interface RTC {
  call: Call | undefined;
  connect: () => Promise<void>;
  dial: (params: { callId: string; destinationNumber: string }) => Promise<void>;
  state: CallState;
  client: TelnyxRTC | null;
  destinationNumber: string | null;
  setDestinationNumber: React.Dispatch<React.SetStateAction<string | null>>;
  setCallData: React.Dispatch<React.SetStateAction<{ call: Call | undefined }>>;
  setMuted: React.Dispatch<React.SetStateAction<boolean>>;
  setDeafened: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useRTC = (): RTC => {
  const clientRef = useRef<TelnyxRTC | null>(null);
  const callCredential = useSelector((state: State) => state.flow.callCredential);

  const [callData, setCallData] = useState<{ call: Call | undefined }>({ call: undefined });
  const [destinationNumber, setDestinationNumber] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);

  const callRef = useRef(callData);
  callRef.current = callData;

  const isDev = window.location.origin.includes('dev');

  const login = callCredential?.username || '';
  const password = callCredential?.password || '';

  const call = callRef.current.call;
  const isInitiating = Boolean(call?.state === 'new' || call?.state === 'requesting');
  const isRinging = Boolean(call?.state === 'trying');
  const isHeld = Boolean(call?.state === 'held');
  const isAnswered = Boolean(call?.state === 'active');
  const isBusy = Boolean(call?.state === 'hangup' && call?.prevState === 'trying');
  const isPurged = Boolean(call?.state === 'purge');
  const contactNumber = call?.options?.remoteCallerNumber;
  const isCallerHangup = Boolean(call?.state === 'hangup' && call?.prevState !== 'trying');
  const isCalleeHangup = Boolean(call?.state === 'destroy' && call?.prevState === 'hangup');
  const isHungup = isCallerHangup || isCalleeHangup;
  const inProgress = Boolean(call && !isHungup);

  // required to ensure the browser doesnt freeze up due to too many connections
  useEffect(() => {
    disconnect();

    return () => {
      disconnect();
    };
  }, [callCredential]);

  const state: CallState = {
    inProgress,
    isInitiating,
    isRinging,
    isHeld,
    isAnswered,
    isHungup,
    isBusy,
    isPurged,
    contactNumber,
    isMuted: muted,
    isDeafened: deafened,
  };

  // cleanup last call
  const disconnect = async () => {
    if (!clientRef.current) return;
    clientRef.current.off('telnyx.ready');
    clientRef.current.off('telnyx.error');
    clientRef.current.off('telnyx.notification');
    resetState();
    await clientRef.current.disconnect();
  };

  const resetState = () => {
    setMuted(false);
    setDeafened(false);
    setCallData({ call: undefined });
  };

  const connect = async () => {
    if (clientRef.current?.connected) return;

    const rtc = TelnyxRTC as any;
    const client = new rtc({
      login,
      password,
      host: isDev ? 'rtcdev.telnyx.com' : undefined,
    });

    client.enableMicrophone();

    // Return a promise that resolves when the 'telnyx.ready' event is emitted
    const waitForReady = new Promise<void>((resolve, reject) => {
      client
        .on('telnyx.ready', () => {
          console.log('ready to call');
          resolve();
        })
        .on('telnyx.error', (callError: { error: CallError; sessionId: string }) => {
          try {
            reject({
              ...callError.error,
              message: `Failed to connect to rtc.telnyx.com: ${callError.error.message}`,
              type: 'call',
            });
          } catch (e) {
            reject({ code: 10027, message: 'An unexpected error occurred while initiating the call.', type: 'call' });
          }
        });
    });

    clientRef.current = client;

    await client.connect();
    await waitForReady;
  };

  const dial = async ({ callId, destinationNumber }: { callId: string; destinationNumber: string }) => {
    if (!clientRef.current) return;
    if (!callId) return;

    resetState();

    // hangup any active calls before starting a new call
    if (callRef?.current && typeof callRef?.current?.call?.hangup === 'function') {
      try {
        callRef.current.call.hangup();
      } catch (e) {
        console.error('Error hanging up previous call:', e);
      }
    }

    return new Promise<void>((resolve, reject) => {
      if (!clientRef.current) return resolve();

      // Listen for call updates through notifications
      clientRef.current?.on('telnyx.notification', (notification: any) => {
        if (notification.type === 'userMediaError') {
          reject({
            message:
              'User media not found. Please ensure an audio device is connected to your machine to make a test call.',
            code: 200,
            type: 'media',
          });
        }

        if (notification.type === 'callUpdate') {
          setCallData({ call: notification.call });

          // checking if an error response was returned
          if (notification.call && notification.call.state === 'destroy') {
            const sipCode = notification.call.sipCode;
            const sipReason = notification.call.sipReason;
            const ignoreSipReason = ['Decline', 'Ringing', 'Temporarily Unavailable'];

            if (!ignoreSipReason.includes(sipReason) && sipCode && sipReason) {
              reject({
                message: sipReason,
                code: sipCode,
                type: 'call',
              });
            }
          }
        }
      });

      // Initiate the call
      const call = clientRef.current.newCall({
        destinationNumber,
        callerNumber: destinationNumber,
        customHeaders: [
          {
            name: 'X-call-id',
            value: callId,
          },
        ],
        audio: true,
      });

      setCallData({ call });
    });
  };

  return {
    call: callRef.current.call,
    connect,
    dial,
    state,
    client: clientRef.current,
    destinationNumber,
    setDestinationNumber,
    setCallData,
    setMuted,
    setDeafened,
  };
};

export const DialogAudio: React.FC<{ stream: MediaStream | undefined }> = ({ stream }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (stream && audioRef.current && audioRef.current.srcObject !== stream) {
      audioRef.current.srcObject = stream;
    }

    return () => {
      if (audioRef.current && audioRef.current.srcObject) {
        const tracks = (audioRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
        audioRef.current.srcObject = null;
      }
    };
  }, [stream]);

  return <audio autoPlay ref={audioRef} id='remoteAudio' />;
};
